import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { PHARMACY_COPY, severityLabel, statusLabel } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock, formatNumber } from '../ui';
import DispensationOriginBadge, { formatConsultationRef } from '../components/DispensationOriginBadge';
import PharmacyDispensationCreatePage from './PharmacyDispensationCreatePage';
import PharmacyDispensationDetailPage from './PharmacyDispensationDetailPage';
import {
  DISPENSATION_STATUSES,
  SOURCE_FILTERS,
  dispensationStatusClass,
  nextDispenseActionLabel,
  severityPillClass,
  useDispensationRoutes
} from '../components/dispensationUi';

const copy = PHARMACY_COPY.DISPENSATIONS;

export default function PharmacyDispensationsPage({
  hidePageHeader = false,
  siteScoped = false,
  hideSiteColumn = false,
  embedded = false,
  openCreateSignal = 0,
  defaultStatusFilter = 'PENDING'
} = {}) {
  const navigate = useNavigate();
  const routes = useDispensationRoutes();
  const { workSiteId, workSites, reloadNonce, mode } = usePharmacy();
  const lockToWorkSite = siteScoped || mode === 'site';

  const [rows, setRows] = useState([]);
  const [statsRows, setStatsRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(defaultStatusFilter || 'PENDING');
  const [siteFilter, setSiteFilter] = useState(() => (workSiteId ? String(workSiteId) : ''));
  const [siteFilterLockedToWork, setSiteFilterLockedToWork] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [embeddedView, setEmbeddedView] = useState('list');
  const [embeddedDetailId, setEmbeddedDetailId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 220);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (lockToWorkSite) {
      setSiteFilter(workSiteId ? String(workSiteId) : '');
      setSiteFilterLockedToWork(true);
      return;
    }
    if (!siteFilterLockedToWork) return;
    setSiteFilter(workSiteId ? String(workSiteId) : '');
  }, [workSiteId, siteFilterLockedToWork, lockToWorkSite]);

  const onSiteFilterChange = (value) => {
    if (lockToWorkSite) return;
    setSiteFilter(value);
    setSiteFilterLockedToWork(Boolean(workSiteId) && value === String(workSiteId));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const lockedToWorkSite =
        lockToWorkSite || siteFilterLockedToWork || (workSiteId && siteFilter === String(workSiteId));
      if (lockToWorkSite && !workSiteId) {
        setRows([]);
        setStatsRows([]);
        return;
      }
      const scopeParams = {};
      if (lockToWorkSite && workSiteId) scopeParams.site_id = workSiteId;
      else if (siteFilter) scopeParams.site_id = siteFilter;

      const listParams = { ...scopeParams, status: statusFilter || undefined };
      if (sourceFilter === 'PRESCRIPTION') listParams.from_prescription = '1';
      else if (sourceFilter === 'MANUAL') listParams.from_prescription = '0';

      const [listRes, statsRes] = await Promise.all([
        pharmacyService.listDispensations(listParams),
        pharmacyService.listDispensations(scopeParams)
      ]);
      setRows(listRes.success ? listRes.data || [] : []);
      setStatsRows(statsRes.success ? statsRes.data || [] : []);
    } finally {
      setLoading(false);
    }
  }, [
    statusFilter,
    sourceFilter,
    siteFilter,
    siteFilterLockedToWork,
    lockToWorkSite,
    workSiteId,
    reloadNonce
  ]);
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (openCreateSignal > 0) {
      if (embedded) setEmbeddedView('create');
      else navigate(routes.NEW);
    }
  }, [openCreateSignal, embedded, navigate, routes.NEW]);

  const filteredRows = useMemo(() => {
    const list = !search
      ? rows
      : rows.filter((d) => {
          const hay = `${d.dispensation_number || ''} ${d.patient_name || ''}`.toLowerCase();
          return hay.includes(search);
        });
    return [...list].sort((a, b) => Number(b.id) - Number(a.id));
  }, [rows, search]);

  const stats = useMemo(() => {
    const pending = statsRows.filter((d) => d.status === 'PENDING').length;
    const dispensed = statsRows.filter((d) => d.status === 'DISPENSED').length;
    const rxPending = statsRows.filter(
      (d) => d.status === 'PENDING' && d.source_type === 'PRESCRIPTION'
    ).length;
    return { total: statsRows.length, pending, dispensed, rxPending };
  }, [statsRows]);

  const openCreate = () => {
    if (embedded) setEmbeddedView('create');
    else navigate(routes.NEW);
  };

  const openDetail = (d) => {
    if (embedded) {
      setEmbeddedDetailId(d.id);
      setEmbeddedView('detail');
    } else navigate(routes.DETAIL(d.id));
  };

  const workSite = useMemo(
    () => workSites.find((s) => String(s.id) === String(workSiteId)) || null,
    [workSites, workSiteId]
  );

  const otherSites = useMemo(
    () => workSites.filter((s) => String(s.id) !== String(workSiteId)),
    [workSites, workSiteId]
  );

  const showSiteColumn =
    !hideSiteColumn || !siteFilter || String(siteFilter) !== String(workSiteId);

  const needsWorkSite = lockToWorkSite && !workSiteId;

  if (embedded && embeddedView === 'create') {
    return (
      <PharmacyDispensationCreatePage
        embedded
        onDone={() => {
          setEmbeddedView('list');
          load();
        }}
      />
    );
  }

  if (embedded && embeddedView === 'detail' && embeddedDetailId) {
    return (
      <PharmacyDispensationDetailPage
        embedded
        dispensationIdProp={String(embeddedDetailId)}
        onBack={() => {
          setEmbeddedView('list');
          setEmbeddedDetailId(null);
          load();
        }}
      />
    );
  }

  return (
    <div className={`cp-dispense-page${embedded ? ' cp-dispense-page--embedded' : ''}`}>
      {!hidePageHeader && !embedded ? (
        <PharmacyPageHeader
          badge={copy.BADGE}
          title={copy.TITLE}
          description={copy.DESC}
          actions={
            <button type="button" className="cp-btn cp-btn--primary" onClick={openCreate} disabled={!workSiteId}>
              <Icon name="plus" size={16} aria-hidden />
              {copy.NEW_BUTTON}
            </button>
          }
        />
      ) : null}

      {embedded ? (
        <div className="cp-dispense-embedded-toolbar">
          <button type="button" className="cp-btn cp-btn--primary cp-btn--sm" onClick={openCreate} disabled={!workSiteId}>
            <Icon name="plus" size={14} aria-hidden />
            {copy.NEW_BUTTON}
          </button>
        </div>
      ) : null}

      <section className="cp-catalog-stats" aria-label="Synthèse dispensations">
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.STAT_TOTAL}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.total)}</span>
        </article>
        <article className="cp-catalog-stat cp-catalog-stat--amber">
          <span className="cp-catalog-stat__label">{copy.STAT_PENDING}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.pending)}</span>
        </article>
        <article className="cp-catalog-stat cp-catalog-stat--ok">
          <span className="cp-catalog-stat__label">{copy.STAT_DISPENSED}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.dispensed)}</span>
        </article>
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.STAT_RX_PENDING}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.rxPending)}</span>
        </article>
      </section>

      <div className="cp-card">
        <div className="cp-catalog-toolbar cp-catalog-toolbar--stack">
          <div className="cp-search-wrap">
            <Icon name="search" size={16} className="cp-search-wrap__icon" aria-hidden />
            <input
              type="search"
              className="cp-search-wrap__input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_ARIA}
            />
          </div>
          <div className="cp-catalog-toolbar__end cp-catalog-toolbar__end--wrap">
            {!lockToWorkSite ? (
              <Form.Select
                size="sm"
                className="cp-dispense-site-filter"
                value={siteFilter}
                onChange={(e) => onSiteFilterChange(e.target.value)}
                aria-label={copy.FILTER_SITE_ARIA}
              >
                <option value="">{copy.FILTER_SITE_ALL}</option>
                {workSiteId ? (
                  <option value={String(workSiteId)}>
                    {copy.FILTER_SITE_WORK}
                    {workSite?.name ? ` — ${workSite.name}` : ''}
                  </option>
                ) : null}
                {otherSites.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
            ) : null}            <div className="cp-filter-pills" role="group" aria-label="Statut dispensation">
              {DISPENSATION_STATUSES.map((s) => (
                <button
                  key={s || 'all'}
                  type="button"
                  className={`cp-filter-pill${statusFilter === s ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter(s)}
                >
                  {s === '' ? copy.FILTER_ALL : statusLabel(s)}
                </button>
              ))}
            </div>
            <div className="cp-filter-pills" role="group" aria-label="Origine dispensation">
              {SOURCE_FILTERS.map((s) => (
                <button
                  key={s.value || 'all-src'}
                  type="button"
                  className={`cp-filter-pill cp-filter-pill--sub${sourceFilter === s.value ? ' is-active' : ''}`}
                  onClick={() => setSourceFilter(s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="cp-icon-btn"
              onClick={load}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={17} aria-hidden />
            </button>
            <div className="cp-catalog-meta">
              <strong>{loading ? '—' : formatNumber(filteredRows.length)}</strong>
              <span>{copy.RESULTS_LABEL}</span>
            </div>
          </div>
        </div>

        <div className="cp-card__body cp-card__body--flush">
          {needsWorkSite ? (
            <div className="cp-empty-state">
              <p className="cp-empty-state__text mb-0">{copy.SITE_REQUIRED}</p>
            </div>
          ) : loading ? (
            <LoadingBlock label="Chargement des dispensations…" />
          ) : filteredRows.length === 0 ? (
            <div className="cp-empty-state">
              <span className="cp-empty-state__icon" aria-hidden>
                <Icon name="pills" size={28} />
              </span>
              <h3 className="cp-empty-state__title">{copy.EMPTY_TITLE}</h3>
              <p className="cp-empty-state__text">
                {sourceFilter === 'PRESCRIPTION' ? copy.EMPTY_RX_HINT : copy.EMPTY_HINT}
              </p>
              {!embedded ? (
                <button type="button" className="cp-btn cp-btn--primary" onClick={openCreate} disabled={!workSiteId}>
                  <Icon name="plus" size={16} aria-hidden />
                  {copy.EMPTY_CTA}
                </button>
              ) : null}
            </div>
          ) : (
            <div className="cp-table-wrap">
              <Table responsive className="cp-table cp-dispense-table mb-0">
                <thead>
                  <tr>
                    <th>{copy.COL_NUMBER}</th>
                    <th>{copy.COL_ORIGIN}</th>
                    <th>{copy.COL_PATIENT}</th>
                    {showSiteColumn ? <th>{copy.COL_SITE}</th> : null}
                    <th>{copy.COL_CONSULT}</th>
                    <th className="text-end">{copy.COL_LINES}</th>
                    <th>{copy.COL_INTERACTION}</th>
                    <th>{copy.COL_STATUS}</th>
                    <th className="text-end">{copy.COL_ACTIONS}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((d) => (
                    <tr
                      key={d.id}
                      className={`cp-dispense-table__row${d.status === 'PENDING' && d.source_type === 'PRESCRIPTION' ? ' is-rx-pending' : ''}`}
                      onClick={() => openDetail(d)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openDetail(d);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                    >
                      <td>
                        <div className="cp-med-cell">
                          <span className="cp-med-cell__icon" aria-hidden>
                            <Icon name="pills" size={16} />
                          </span>
                          <span className="cp-med-cell__body">
                            <span className="cp-med-cell__name">{d.dispensation_number}</span>
                            <span className="cp-med-cell__desc">{nextDispenseActionLabel(d.status)}</span>
                          </span>
                        </div>
                      </td>
                      <td>
                        <DispensationOriginBadge sourceType={d.source_type} />
                      </td>
                      <td>{d.patient_name || '—'}</td>
                      {showSiteColumn ? <td>{d.site_name}</td> : null}
                      <td className="cp-consult-ref">{formatConsultationRef(d.consultation_id) || '—'}</td>
                      <td className="text-end cp-num">{formatNumber(d.items_count)}</td>
                      <td>
                        {d.interaction_flag ? (
                          <span className={`cp-status-pill ${severityPillClass(d.interaction_severity)}`}>
                            {severityLabel(d.interaction_severity)}
                          </span>
                        ) : (
                          <span className="cp-status-pill cp-status-pill--ok">{copy.INTERACTION_NONE}</span>
                        )}
                      </td>
                      <td>
                        <span className={`cp-status-pill ${dispensationStatusClass(d.status)}`}>
                          {statusLabel(d.status)}
                        </span>
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className={`cp-btn cp-btn--sm ${d.status === 'PENDING' ? 'cp-btn--primary' : 'cp-btn--ghost'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetail(d);
                          }}
                        >
                          {d.status === 'PENDING' ? copy.TREAT : copy.OPEN}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
